import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { StandardButton } from '../styles/StandardButtons';
import { AuthBanner } from './AuthBanner';
import { supabase } from '../lib/supabase';
import './Setup.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isAlreadyRegisteredError(message: string): boolean {
  const lower = (message || '').toLowerCase();
  return (
    lower.includes('already registered') ||
    lower.includes('user already exists') ||
    lower.includes('email already registered')
  );
}

// Deliberately just email + password. first_name/last_name/company_name
// used to be collected here too, duplicating what OnboardingWizard asks
// right after -- removed rather than kept, since Supabase Auth requires
// email confirmation before a session exists (so this form can never
// call onboard_company() directly anyway). The real "signup" experience
// is this form -> confirm email -> land back in the app already signed
// in -> OnboardingWizard (company + user info), which is the true
// continuation of signup, not a separate later step.
export function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsDuplicateEmail(false);
    setLoading(true);

    const em = email.trim();

    if (!em) {
      setError('Email is required.');
      setLoading(false);
      return;
    }
    if (!EMAIL_REGEX.test(em)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      setLoading(false);
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: em,
        password,
      });

      if (signUpError) {
        const msg = signUpError.message || 'Sign up failed.';
        setError(isAlreadyRegisteredError(msg) ? 'An account with this email already exists. Please log in.' : msg);
        setIsDuplicateEmail(isAlreadyRegisteredError(msg));
        setLoading(false);
        return;
      }

      if (data?.user) {
        navigate('/auth/signup/success', { replace: true });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign up failed.';
      setError(isAlreadyRegisteredError(msg) ? 'An account with this email already exists. Please log in.' : msg);
      setIsDuplicateEmail(isAlreadyRegisteredError(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-container">
      {/* unmapped design value: 28rem also hardcoded in OnboardingWizard.tsx/SignupSuccessPage.tsx's setup-content, no shared token exists for it */}
      <div className="setup-content" style={{ maxWidth: '28rem' }}>
        <AuthBanner />
        <h1 className="h1">Create your account</h1>

        {error && (
          <div className="error-message" role="alert">
            {error}
            {isDuplicateEmail && (
              <p className="mt-2">
                <Link to="/login" className="text-blue-600 hover:underline">
                  Go to login
                </Link>
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <small className="text-muted">Minimum 8 characters</small>
          </div>

          <div className="mt-4 flex justify-end">
            <StandardButton customVariant="black" type="submit" disabled={loading}>
              {loading ? 'Signing up…' : 'Sign up'}
            </StandardButton>
          </div>
        </form>

        <p className="mt-4 text-sm text-gray-600">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
